import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Typography,
  Container,
  Box,
  CardContent,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import {
  Landscape,
  CalendarMonth,
  Scale,
  Close,
  Spa, Forest, HelpOutline,
  ExpandLess,
  ExpandMore
} from "@mui/icons-material";
import moment from "moment";

export const CardContentData = ({ dialogbox, setDialogbox, pluckingChartReportData, closeDialogbox, selectFieldID, fromDate, toDate }) => {
  const getBorderColor = (value, qty) => {
    if (value > 7) return '#c40202';
    else if (value === 0 && qty === 0) return '#56585c';
    return '#0e8c16';
  };

  const LineChart = ({ items = [], strokeColor = '#0e8c16', itemSpacing = 60, height = 160 }) => {
    if (!items || items.length === 0) return null;
    const count = items.length;
    const padLeft = 44;
    const padRight = 12;
    const padTop = 10;
    const padBottom = 36;

    const chartWidth = Math.max(padLeft + padRight + count * itemSpacing, 600);

    const values = items.map(i => i.value || 0);
    const rawMax = Math.max(...values, 1);
    const step = 50;
    const yMax = Math.ceil(rawMax / step) * step || step;
    const tickValues = [];
    for (let v = yMax; v >= 0; v -= step) tickValues.push(v);
    const ticks = Math.max(1, tickValues.length - 1);

    const extraPerTick = 10;
    const baselineTicks = 3;
    const extraHeight = Math.max(0, (tickValues.length - baselineTicks) * extraPerTick);

    const chartHeight = height + extraHeight;

    const plotWidth = chartWidth - padLeft - padRight;
    const plotHeight = chartHeight - padTop - padBottom;

    const pts = items.map((d, i) => {
      const spacing = plotWidth / count;
      const x = padLeft + (i + 0.5) * spacing;
      const norm = yMax > 0 ? (d.value || 0) / yMax : 0;
      const y = padTop + (1 - norm) * plotHeight;
      return { x, y, v: d.value, label: d.date };
    });

    const polyPoints = pts.map(p => `${p.x},${p.y}`).join(' ');
    const areaPoints = `${polyPoints} ${padLeft + plotWidth},${padTop + plotHeight} ${padLeft},${padTop + plotHeight}`;

    return (
      <Box sx={{ width: chartWidth, mb: 1 }}>
        <Box sx={{ width: chartWidth }}>
          <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} width={chartWidth} height={chartHeight} preserveAspectRatio="xMinYMin meet">
            {tickValues.map((tv, idx) => {
              const y = padTop + (idx / ticks) * plotHeight;
              return (
                <g key={idx}>
                  <line x1={padLeft} y1={y} x2={padLeft + plotWidth} y2={y} stroke="#eee" strokeWidth={0.8} />
                  <text x={padLeft - 8} y={y + 4} fontSize={11} textAnchor="end" fill="#666">{tv.toLocaleString()}</text>
                </g>
              );
            })}

            <polygon points={areaPoints} fill={`${strokeColor}22`} stroke="none" />

            <polyline points={polyPoints} fill="none" stroke={strokeColor} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

            {pts.map((p, i) => (
              <line key={`tick-${i}`} x1={p.x} y1={padTop + plotHeight} x2={p.x} y2={padTop + plotHeight + 6} stroke="#ddd" strokeWidth={1} />
            ))}

            {pts.map((p, i) => (
              <g key={i}>
                <circle cx={p.x} cy={p.y} r={3} fill={strokeColor} />
                <title>{`${p.label}: ${p.v.toLocaleString()}`}</title>
              </g>
            ))}

            <line x1={padLeft} y1={padTop} x2={padLeft} y2={padTop + plotHeight} stroke="#cfcfcf" strokeWidth={1} />
          </svg>
        </Box>

        <Box sx={{ width: chartWidth }}>
          <Box sx={{ display: 'flex', minWidth: chartWidth }}>
            <Box sx={{ minWidth: padLeft }} />
            <Box sx={{ display: 'flex', minWidth: chartWidth - padLeft - padRight }}>
              {items.map((d, i) => (
                <Box key={i} sx={{ width: itemSpacing, textAlign: 'center' }} title={d.date}>
                  <Typography variant="caption" sx={{ display: 'block', lineHeight: '14px' }}>{moment(d.date).format('DD')}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', lineHeight: '14px' }}>{moment(d.date).format('MMM')}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const handleClose = () => {
    setDialogbox(false);
    closeDialogbox();
  };

  const [expandedGroups, setExpandedGroups] = React.useState([]);

  const selectedField = (pluckingChartReportData || []).find(
    item => item.fieldID === selectFieldID
  );
  const details = selectedField?.details || [];
  const detailsPlucking = details.filter(d => d.jobTypeID === 1);
  const detailsNonPlucking = details.filter(d => d.jobTypeID === 2);
  const hasDetails = (detailsPlucking[0]?.empCount === 0);
  const hasNonDetails = (detailsNonPlucking[0]?.empCount === 0);

  const pluckingChartItems = React.useMemo(() => {
    const sorted = [...detailsPlucking].sort((a, b) => new Date(a.pluckingDate) - new Date(b.pluckingDate));
    return sorted.map(d => ({ date: moment(d.pluckingDate).format('YYYY-MM-DD'), value: d.dayQuntity || 0 }));
  }, [detailsPlucking]);

  const [selectedMonth, setSelectedMonth] = React.useState('all');
  const monthOptions = React.useMemo(() => {
    const months = Array.from(new Set(pluckingChartItems.map(it => moment(it.date).format('YYYY-MM'))));
    months.sort();
    return [
      { value: 'all', label: 'All' },
      ...months.map(m => ({ value: m, label: moment(m + '-01').format('MMM YYYY') }))
    ];
  }, [pluckingChartItems]);

  const filteredPluckingItems = React.useMemo(() => {
    if (!selectedMonth || selectedMonth === 'all') return pluckingChartItems;
    return pluckingChartItems.filter(it => moment(it.date).format('YYYY-MM') === selectedMonth);
  }, [pluckingChartItems, selectedMonth]);

  const ITEM_SPACING = 60;
  const PAD_LEFT = 44;
  const PAD_RIGHT = 12;
  const chartContainerMinWidth = Math.max(PAD_LEFT + PAD_RIGHT + (filteredPluckingItems.length || 0) * ITEM_SPACING, 600);

  const firstDetail = details[0] || {};
  const {
    fieldName,
    productName,
    totalQuntity
  } = firstDetail;

  const getProductIcon = (productName) => {
    switch ((productName || "").toLowerCase()) {
      case 'tea':
        return <Spa color="primary" sx={{ mr: 1 }} />;
      case 'rubber':
        return <Forest color="primary" sx={{ mr: 1 }} />;
      default:
        return <HelpOutline color="primary" sx={{ mr: 1 }} />;
    }
  };

  return (
    <Dialog
      fullWidth
      open={dialogbox}
      onClose={handleClose}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
      PaperProps={{
        style: {
          width: 1000,
          maxWidth: '97vw',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={600} color={getBorderColor(detailsPlucking[0]?.differentDaysFromLastPluck, detailsPlucking[0]?.totalQuntity)}>
            Field Information
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Container>
            <Box mt={1}>
              <CardContent
                style={{
                  border: `2px solid ${getBorderColor(details[0]?.differentDaysFromLastPluck, details[0]?.totalQuntity)}`,
                  boxShadow: `0 4px 16px ${getBorderColor(details[0]?.differentDaysFromLastPluck, details[0]?.totalQuntity)}33, 0 1.5px 6px rgba(0,0,0,0.08)`,
                  borderRadius: 18,
                  background:
                    details[0]?.differentDaysFromLastPluck > 7
                      ? 'linear-gradient(135deg, #fff0f0 0%, #ffe0e0 100%)'
                      : (details[0]?.differentDaysFromLastPluck === 0 && details[0]?.totalQuntity === 0)
                        ? 'linear-gradient(135deg, #f2f2f2 0%, #e1e2e6 100%)'
                        : 'linear-gradient(135deg, #f8fff8 0%, #e6ffe6 100%)',
                  margin: '0 auto',
                  maxWidth: 900,
                  padding: 32,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center">
                      <Landscape color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        <strong>Field Name : </strong> {fieldName || "-"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center">
                      {getProductIcon(productName)}
                      <Typography variant="body1">
                        <strong>Product Name : </strong> {productName || "-"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center">
                      <Scale color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        <strong>Total Quantity Plucked : </strong> {totalQuntity.toLocaleString() || 0}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center">
                      <CalendarMonth color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        <strong>Date Range :</strong> {fromDate} to {toDate}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <br />
                {!hasDetails && (
                  <Box
                    sx={{
                      width: '100%',
                      overflowX: 'auto',
                      mb: 2,
                      whiteSpace: 'nowrap',
                      scrollbarWidth: 'thin',
                      '&::-webkit-scrollbar': { height: 6 },
                      '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: 3 },
                    }}
                  >
                    <Box sx={{ display: 'inline-block', width: chartContainerMinWidth, px: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                          <InputLabel id="month-select-label">Month</InputLabel>
                          <Select
                            labelId="month-select-label"
                            value={selectedMonth}
                            label="Month"
                            onChange={e => setSelectedMonth(e.target.value)}
                          >
                            {monthOptions.map(m => (
                              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      <LineChart items={filteredPluckingItems} strokeColor={getBorderColor(detailsPlucking[0]?.differentDaysFromLastPluck, detailsPlucking[0]?.totalQuntity)} />
                    </Box>
                  </Box>
                )}
                <Grid container spacing={6}>
                  {!hasDetails && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" fontWeight={500} gutterBottom>
                        <strong><u>Plucking Details</u></strong>
                      </Typography>
                      <Box sx={{ overflowX: 'auto' }}>
                        {(() => {
                          const borderColor = getBorderColor(detailsPlucking[0]?.differentDaysFromLastPluck, detailsPlucking[0]?.totalQuntity);
                          let headerBg = '#e6ffe6';
                          if (detailsPlucking[0]?.differentDaysFromLastPluck > 7) headerBg = '#ffe0e0';
                          else if (detailsPlucking[0]?.differentDaysFromLastPluck === 0 && detailsPlucking[0]?.totalQuntity === 0) headerBg = '#e1e2e6';
                          let rowBgEven = '#f8fff8', rowBgOdd = '#e6ffe6';
                          if (detailsPlucking[0]?.differentDaysFromLastPluck > 7) { rowBgEven = '#fff0f0'; rowBgOdd = '#ffe0e0'; }
                          else if (detailsPlucking[0]?.differentDaysFromLastPluck === 0 && detailsPlucking[0]?.totalQuntity === 0) { rowBgEven = '#f2f2f2'; rowBgOdd = '#e1e2e6'; }

                          const totalEmp = detailsPlucking.reduce((sum, row) => sum + (row.empCount || 0), 0);
                          const totalKg = detailsPlucking.reduce((sum, row) => sum + (row.dayQuntity || 0), 0);

                          const key = 'plucking';
                          const isExpanded = expandedGroups.includes(key);
                          const handleToggle = () => {
                            setExpandedGroups(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
                          };

                          if (detailsPlucking.length === 0 || (detailsPlucking[0]?.empCount === 0 && detailsPlucking[0]?.dayQuntity === 0)) {
                            return (
                              <table style={{ width: '100%' }}>
                                <tbody>
                                  <tr>
                                    <td colSpan={3} style={{ textAlign: 'center', padding: 10 }}>No Plucking Data Available.</td>
                                  </tr>
                                </tbody>
                              </table>
                            );
                          }

                          const sorted = [...detailsPlucking].sort((a, b) => new Date(a.pluckingDate) - new Date(b.pluckingDate));

                          return (
                            <table style={{ width: '100%', borderCollapse: 'collapse', border: `1.5px solid ${borderColor}`, borderRadius: 10, overflow: 'hidden' }}>
                              <thead>
                                <tr style={{ background: headerBg }}>
                                  <th style={{ padding: 10, textAlign: 'left', fontWeight: 600, color: borderColor, borderBottom: `2px solid ${borderColor}` }}>Plucking</th>
                                  <th style={{ padding: 10, textAlign: 'center', fontWeight: 600, color: borderColor, borderBottom: `2px solid ${borderColor}` }}>Employee Count</th>
                                  <th style={{ padding: 10, textAlign: 'center', fontWeight: 600, color: borderColor, borderBottom: `2px solid ${borderColor}` }}>Kilo Plucked</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr style={{ background: rowBgEven, cursor: 'pointer' }} onClick={handleToggle}>
                                  <td style={{ padding: 10 }}>Plucking</td>
                                  <td style={{ padding: 10, textAlign: 'center', fontWeight: 600 }}>{totalEmp}</td>
                                  <td style={{ padding: 10, textAlign: 'center', fontWeight: 600 }}>{totalKg.toLocaleString()}</td>
                                </tr>
                                {isExpanded && (
                                  <tr>
                                    <td colSpan={3} style={{ padding: 0 }}>
                                      <table style={{ width: '100%', borderCollapse: 'collapse', border: `1.5px solid ${borderColor}`, borderRadius: 10, overflow: 'hidden', marginTop: 8 }}>
                                        <thead>
                                          <tr style={{ background: headerBg }}>
                                            <th style={{ padding: 10, textAlign: 'left', fontWeight: 600, color: borderColor, width: '150px' }}>Date</th>
                                            <th style={{ padding: 10, textAlign: 'center', fontWeight: 600, color: borderColor }}>Employee Count</th>
                                            <th style={{ padding: 10, textAlign: 'center', fontWeight: 600, color: borderColor }}>Kilo Plucked</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {sorted.map((row, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid #e0e0e0', background: i % 2 === 0 ? rowBgEven : rowBgOdd }}>
                                              <td style={{ padding: 10 }}>{moment(row.pluckingDate).format('YYYY-MM-DD') || '-'}</td>
                                              <td style={{ padding: 10, textAlign: 'center' }}>{row.empCount || 0}</td>
                                              <td style={{ padding: 10, textAlign: 'center' }}>{(row.dayQuntity || 0).toLocaleString()}</td>
                                            </tr>
                                          ))}
                                          <tr>
                                            <td style={{ padding: 10, fontWeight: 600, color: borderColor }}>Total</td>
                                            <td style={{ padding: 10, textAlign: 'center', fontWeight: 600, color: borderColor }}>{totalEmp}</td>
                                            <td style={{ padding: 10, textAlign: 'center', fontWeight: 600, color: borderColor }}>{totalKg.toLocaleString()}</td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          );
                        })()}
                      </Box>
                    </Grid>
                  )}
                  {!hasNonDetails && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" fontWeight={500} gutterBottom>
                        <strong><u>Non Plucking Details</u></strong>
                      </Typography>
                      <Box sx={{ overflowX: 'auto' }}>
                        {(() => {
                          const groups = {};
                          detailsNonPlucking.forEach(row => {
                            const key = row.taskCode;
                            if (!groups[key]) groups[key] = [];
                            groups[key].push(row);
                          });
                          const groupKeys = Object.keys(groups);
                          const handleToggleGroup = key => {
                            setExpandedGroups(prev =>
                              prev.includes(key)
                                ? prev.filter(k => k !== key)
                                : [...prev, key]
                            );
                          };
                          const borderColor = getBorderColor(detailsPlucking[0]?.differentDaysFromLastPluck, detailsPlucking[0]?.totalQuntity);
                          let headerBg = '#e6ffe6';
                          if (detailsPlucking[0]?.differentDaysFromLastPluck > 7) headerBg = '#ffe0e0';
                          else if (detailsPlucking[0]?.differentDaysFromLastPluck === 0 && detailsPlucking[0]?.totalQuntity === 0) headerBg = '#e1e2e6';
                          let rowBgEven = '#f8fff8', rowBgOdd = '#e6ffe6';
                          if (detailsPlucking[0]?.differentDaysFromLastPluck > 7) { rowBgEven = '#fff0f0'; rowBgOdd = '#ffe0e0'; }
                          else if (detailsPlucking[0]?.differentDaysFromLastPluck === 0 && detailsPlucking[0]?.totalQuntity === 0) { rowBgEven = '#f2f2f2'; rowBgOdd = '#e1e2e6'; }
                          if (groupKeys.length === 0) {
                            return (
                              <table style={{ width: '100%' }}>
                                <tbody>
                                  <tr>
                                    <td colSpan={3} style={{ textAlign: 'center', padding: 10 }}>No Non Plucking Data Available.</td>
                                  </tr>
                                </tbody>
                              </table>
                            );
                          }
                          return (
                            <table style={{ width: '100%', borderCollapse: 'collapse', border: `1.5px solid ${borderColor}`, borderRadius: 10, overflow: 'hidden' }}>
                              <thead>
                                <tr style={{ background: headerBg }}>
                                  <th style={{
                                    padding: 10, textAlign: 'left', fontWeight: 600, color: borderColor,
                                    borderBottom: `2px solid ${getBorderColor(detailsPlucking[0]?.differentDaysFromLastPluck, detailsPlucking[0]?.totalQuntity)}`,
                                  }}>Task Code</th>
                                  <th style={{
                                    padding: 10, textAlign: 'center', fontWeight: 600, color: borderColor,
                                    borderBottom: `2px solid ${getBorderColor(detailsPlucking[0]?.differentDaysFromLastPluck, detailsPlucking[0]?.totalQuntity)}`,
                                  }}>Employee Count</th>
                                  <th style={{
                                    padding: 10, textAlign: 'center', fontWeight: 600, color: borderColor,
                                    borderBottom: `2px solid ${getBorderColor(detailsPlucking[0]?.differentDaysFromLastPluck, detailsPlucking[0]?.totalQuntity)}`,
                                  }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {groupKeys.map((key, idx) => {
                                  const rows = groups[key];
                                  const isExpanded = expandedGroups.includes(key);
                                  return (
                                    <React.Fragment key={key}>
                                      <tr style={{ background: idx % 2 === 0 ? rowBgEven : rowBgOdd, cursor: 'pointer' }} onClick={() => handleToggleGroup(key)}>
                                        <td style={{ padding: 10 }}>{key}</td>
                                        <td style={{ padding: 10, textAlign: 'center', fontWeight: 600 }}>{rows.reduce((sum, row) => sum + (row.empCount || 0), 0)}</td>
                                        <td style={{ padding: 10, textAlign: 'center' }}>
                                          {isExpanded ? <ExpandLess sx={{ color: borderColor }} /> : <ExpandMore sx={{ color: borderColor }} />}
                                        </td>
                                      </tr>
                                      {isExpanded && (
                                        <tr>
                                          <td colSpan={3} style={{ padding: 0 }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', border: `1.5px solid ${borderColor}`, borderRadius: 10, overflow: 'hidden', marginTop: 8 }}>
                                              <thead>
                                                <tr style={{ background: headerBg }}>
                                                  <th style={{
                                                    padding: 10, textAlign: 'left', fontWeight: 600, width: '110px', color: borderColor,
                                                    borderBottom: `2px solid ${getBorderColor(detailsPlucking[0]?.differentDaysFromLastPluck, detailsPlucking[0]?.totalQuntity)}`,
                                                  }}>Date</th>
                                                  <th style={{
                                                    padding: 10, textAlign: 'center', fontWeight: 600, width: '110px', color: borderColor,
                                                    borderBottom: `2px solid ${getBorderColor(detailsPlucking[0]?.differentDaysFromLastPluck, detailsPlucking[0]?.totalQuntity)}`,
                                                  }}>Employee Count</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {rows.map((row, i) => (
                                                  <tr key={i} style={{ borderBottom: '1px solid #e0e0e0', background: i % 2 === 0 ? rowBgEven : rowBgOdd }}>
                                                    <td style={{ padding: 10 }}>{moment(row.pluckingDate).format('YYYY-MM-DD') || '-'}</td>
                                                    <td style={{ padding: 10, textAlign: 'center' }}>{row.empCount || 0}</td>
                                                  </tr>
                                                ))}
                                                <tr>
                                                  <td style={{ padding: 10, fontWeight: 600, color: borderColor }}>Total</td>
                                                  <td style={{ padding: 10, textAlign: 'center', fontWeight: 600, color: borderColor }}>
                                                    {rows.reduce((sum, row) => sum + (row.empCount || 0), 0)}
                                                  </td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          );
                        })()}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Box>
          </Container>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};